import { CaretRight, Ticket } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { currencyFormatter } from "lib/utils/formatters";
import { extractRupeeValue } from "lib/utils/coupons";
import React from "react";
import { PartnerOffersStripProps } from "../couponProps";

const PartnerOffersStrip: React.FC<PartnerOffersStripProps> = ({
  customisations,
  onOpenPartnerOffers,
  partnerOffersConfig,
  showDivider = false,
  withFooterContainer = false,
}) => {
  const {
    state: { partnerOffers, partnerOffersDisplay },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const { t } = useLocale();

  const showPartnerOffers =
    isAuthenticated && Boolean(partnerOffersConfig?.enabled) && (partnerOffers?.length ?? 0) > 0;

  if (!showPartnerOffers) return null;

  const isCollected = (state: string) => state === "COLLECTED" || state === "ALREADY_APPLIED";
  const allPartnerOffersCollected = partnerOffers.every((o) => isCollected(o.offer_state));
  const noneCollected = partnerOffers.every((o) => !isCollected(o.offer_state));
  const collectedCount = partnerOffers.filter((o) => isCollected(o.offer_state)).length;
  const totalCount = partnerOffers.length;
  const displayState = allPartnerOffersCollected
    ? partnerOffersDisplay?.all_collected
    : noneCollected
      ? partnerOffersDisplay?.none_collected
      : partnerOffersDisplay?.partial_collected;
  const highlightText = displayState?.highlights?.[0]?.text;
  const partnerOffersTitle = partnerOffersConfig?.section_title;

  const collectedOffers = partnerOffers.filter((o) => isCollected(o.offer_state));
  const totalClaimedValue = collectedOffers.reduce(
    (sum, o) => sum + (o.offer_value ?? extractRupeeValue(o.title || o.description)),
    0,
  );

  const row = (
    <div
      className="flex py-2 justify-between w-full items-center px-3 cursor-pointer"
      onClick={onOpenPartnerOffers}>
      <div
        className="text-text-xs-medium flex gap-1.5 items-center"
        style={{ color: customisations?.footer?.primaryColor }}>
        <Icon
          IconComponent={Ticket}
          size="xs"
          iconProps={{ color: customisations?.footer?.primaryColor, weight: "fill" }}
        />
        <span>
          {collectedCount > 0
            ? totalClaimedValue > 0
              ? t("partner_offers.worth_vouchers_claimed", {
                  amount: currencyFormatter(totalClaimedValue),
                })
              : t("partner_offers.x_of_y_claimed", {
                  collected: collectedCount,
                  total: totalCount,
                })
            : (highlightText ?? partnerOffersTitle)}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        {collectedCount > 0 ? (
          <span className="text-text-xs-medium" style={{ color: customisations?.footer?.infoColor }}>
            {t("partner_offers.x_of_y_claimed", {
              collected: collectedCount,
              total: totalCount,
            })}
          </span>
        ) : (
          <div className="flex py-px">
            {partnerOffers
              .filter((o) => o.logo_url)
              .map((o, i) => (
                <img
                  key={o.offer_id}
                  src={o.logo_url}
                  alt={o.partner_name ?? o.offer_id}
                  className={`h-6 w-6 object-cover rounded-full border border-black/[0.06]${
                    i !== 0 ? " -ml-2" : ""
                  }`}
                />
              ))}
          </div>
        )}
        <div className="flex h-3.5 w-3.5 items-center justify-center">
          <Icon
            IconComponent={CaretRight}
            size="xxxs"
            className="text-zinc-700/60"
            iconProps={{ weight: "bold", color: customisations?.footer?.infoColor }}
          />
        </div>
      </div>
    </div>
  );

  const content = (
    <>
      {showDivider && <div className="h-px mx-3" style={{ backgroundColor: "rgba(10, 13, 18, 0.08)" }} />}
      {row}
    </>
  );

  if (withFooterContainer) {
    return (
      <div
        className="flex flex-col justify-between border-t w-full"
        style={{ backgroundColor: customisations?.footer?.backgroundColor }}>
        {content}
      </div>
    );
  }

  return content;
};

export default PartnerOffersStrip;
